import React, { useCallback, useContext, useEffect, useMemo, useState, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import AuthContext from '../../context/authContext';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  ListSubheader,
  MenuItem,
  Paper,
  Stack,
  Select,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import InputLabel from '@mui/material/InputLabel';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import DownloadIcon from '@mui/icons-material/Download';
import { currencyFormatter, percentFormatter, decimalFormatter } from '../../util/formatHelper';
import useVoucher from '../../hooks/useVoucher';
import useVoucherProduct from '../../hooks/useVoucherProduct';
import useResourceVoucherProduct from '../../hooks/useResourceVoucherProduct';
import useBranch from '../../hooks/useBranch';
import useUser from '../../hooks/useUser';
import useStatisticJob from '../../hooks/useStatisticJob';
import useStatisticCacheEntries from '../../hooks/useStatisticCacheEntries';
import StatisticViewShell from '../ui/StatisticViewShell';
import {
  getCartesianGridStroke,
  getRechartsAxisStroke,
  getRechartsTooltipContentStyle,
  getRechartsTooltipLabelStyle,
  getStatisticsChartCursorFill,
  getStatisticsKpiHelperColor,
  getStatisticsKpiLabelColor,
  getStatisticsSurfaceBackground,
  getStatisticsTableRowBorderTop,
  getRechartsLegendBarProps,
  getRechartsLegendPieProps,
  getStatisticsHtmlThStyle,
  getStatisticsPageRootStyle,
} from '../../util/chartTheme';
import { STATISTIC_TYPE_VOUCHER, STATISTIC_TYPE_RESOURCE_VOUCHER } from '../../constants/statisticTypes';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const defaultStartDate = dayjs().startOf('month').format('YYYY-MM-DD');
const defaultEndDate = dayjs().endOf('month').format('YYYY-MM-DD');

const formatCurrency = value => currencyFormatter.format(value ?? 0);

const STATUS_LABELS = { active: 'Aktiv', used: 'Eingelöst', expired: 'Abgelaufen', locked: 'Gesperrt' };

const VoucherStatistics = () => {
  const theme = useTheme();
  const chartGridStroke = useMemo(() => getCartesianGridStroke(theme), [theme]);
  const chartTooltipContentStyle = useMemo(() => getRechartsTooltipContentStyle(theme), [theme]);
  const statSurfaceBg = useMemo(() => getStatisticsSurfaceBackground(theme), [theme]);
  const chartCursorFill = useMemo(() => getStatisticsChartCursorFill(theme), [theme]);
  const axisStroke = useMemo(() => getRechartsAxisStroke(theme), [theme]);
  const tooltipLabelStyle = useMemo(() => getRechartsTooltipLabelStyle(theme), [theme]);
  const chartLegendBarProps = useMemo(() => getRechartsLegendBarProps(theme), [theme]);
  const chartLegendPieProps = useMemo(() => getRechartsLegendPieProps(theme), [theme]);
  const htmlThStyle = useCallback((extra = {}) => getStatisticsHtmlThStyle(theme, extra), [theme]);
  const tableRowBorderTop = useMemo(() => getStatisticsTableRowBorderTop(theme), [theme]);
  const kpiLabelColor = useMemo(() => getStatisticsKpiLabelColor(theme), [theme]);
  const kpiHelperColor = useMemo(() => getStatisticsKpiHelperColor(theme), [theme]);
  const colorsPaidGift = useMemo(
    () => [theme.palette.success.main, theme.palette.warning.main],
    [theme]
  );
  const colorsRevenue = useMemo(
    () => [theme.palette.primary.main, theme.palette.secondary.main],
    [theme]
  );
  const sellAndRedeemColors = useMemo(
    () => [theme.palette.success.main, theme.palette.error.main],
    [theme]
  );
  const statusColors = useMemo(
    () => ({
      active: theme.palette.success.main,
      used: theme.palette.primary.main,
      expired: theme.palette.warning.main,
      locked: theme.palette.error.main,
    }),
    [theme]
  );
  const secondaryTextColor = theme.palette.text.secondary;
  const cardStyle = useMemo(
    () => ({
      background: statSurfaceBg,
      borderRadius: '8px',
      padding: '1rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem',
    }),
    [statSurfaceBg]
  );
  const authCtx = useContext(AuthContext);

  // Access model (mirrors the upgrade statistics view): the full right grants
  // the complete, cross-branch view (optionally filtered to one branch); the
  // "own" right is restricted to the user's assigned branch(es) and to
  // redemption figures only.
  const canFullStat = authCtx.checkRight('voucher:special:statistics');
  const canOwnStat = authCtx.checkRight('voucher:special:statisticsOwn');
  const canView = canFullStat || canOwnStat;
  const ownOnly = canOwnStat && !canFullStat;

  const [voucherProducts, setVoucherProducts] = useState([]);
  const [resourceVoucherProducts, setResourceVoucherProducts] = useState([]);
  const [appliedFilter, setAppliedFilter] = useState({
    startDate: defaultStartDate,
    endDate: defaultEndDate,
    selectedFilter: 'balance:all',
    branchId: undefined,
  });
  const [startDate, setStartDate] = useState(dayjs(defaultStartDate));
  const [endDate, setEndDate] = useState(dayjs(defaultEndDate));
  const [selectedFilter, setSelectedFilter] = useState('balance:all');
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [chartType, setChartType] = useState('line');
  const [selectedCacheEntryId, setSelectedCacheEntryId] = useState(null);

  const [branchOptions, setBranchOptions] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [branchesReady, setBranchesReady] = useState(false);
  const [hasOwnAssignedBranches, setHasOwnAssignedBranches] = useState(false);

  const { downloadVoucherStatisticsReport } = useVoucher();
  const { getAllVoucherProducts } = useVoucherProduct();
  const { getAllResourceVoucherProducts } = useResourceVoucherProduct();
  const { getBranches } = useBranch();
  const { getMyBranches } = useUser();

  const getAllVoucherProductsRef = useRef(getAllVoucherProducts);
  const getAllResourceVoucherProductsRef = useRef(getAllResourceVoucherProducts);
  const getBranchesRef = useRef(getBranches);
  const getMyBranchesRef = useRef(getMyBranches);

  useEffect(() => {
    getAllVoucherProductsRef.current = getAllVoucherProducts;
  }, [getAllVoucherProducts]);

  useEffect(() => {
    getAllResourceVoucherProductsRef.current = getAllResourceVoucherProducts;
  }, [getAllResourceVoucherProducts]);

  useEffect(() => {
    getBranchesRef.current = getBranches;
  }, [getBranches]);

  useEffect(() => {
    getMyBranchesRef.current = getMyBranches;
  }, [getMyBranches]);

  // Per-product filtering is only offered to full-access users. The "own"
  // (redemptions-only) view uses a simplified type switch and does not load
  // the product catalogue.
  useEffect(() => {
    if (!canFullStat) return;
    const loadProducts = async () => {
      try {
        const [balanceProducts, rvProducts] = await Promise.all([
          getAllVoucherProductsRef.current(),
          getAllResourceVoucherProductsRef.current(),
        ]);
        setVoucherProducts(balanceProducts);
        setResourceVoucherProducts(rvProducts);
      } catch (error) {
        toast.error('Fehler beim Laden der Gutscheinprodukte');
      }
    };

    loadProducts();
  }, [canFullStat]);

  // Load branch options for the filter bar. Full users get all branches plus an
  // "Alle Standorte" option; own users get their assigned branches (or, if none
  // are assigned, fall back to the full list = full access).
  useEffect(() => {
    let cancelled = false;
    const mapBranch = b => ({
      _id: typeof b._id === 'string' ? b._id : String(b._id),
      label: b.locationName || b.longName || 'Standort',
    });
    const loadFullBranchList = async () => {
      try {
        const list = await getBranchesRef.current();
        if (cancelled) return;
        setBranchOptions([{ _id: '', label: 'Alle Standorte' }, ...(list || []).map(mapBranch)]);
      } catch (_) {
        if (!cancelled) setBranchOptions([{ _id: '', label: 'Alle Standorte' }]);
      }
    };
    (async () => {
      if (!canView) return;
      if (canFullStat) {
        await loadFullBranchList();
        if (!cancelled) setBranchesReady(true);
        return;
      }
      // own-only
      try {
        const r = await getMyBranchesRef.current();
        if (cancelled) return;
        const assignedIds = (r?.branchIds || []).filter(Boolean);
        if (assignedIds.length > 0) {
          setBranchOptions((r?.branches || []).map(mapBranch));
          setHasOwnAssignedBranches(true);
          const id = assignedIds[0];
          setSelectedBranchId(id);
          setAppliedFilter(prev => ({ ...prev, branchId: id }));
        } else {
          setHasOwnAssignedBranches(false);
          await loadFullBranchList();
        }
      } catch (_) {
        if (!cancelled) setBranchOptions([]);
      }
      if (!cancelled) setBranchesReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [canView, canFullStat]);

  const parseFilter = filter => {
    const idx = filter.indexOf(':');
    return {
      type: filter.substring(0, idx),
      productId: filter.substring(idx + 1),
    };
  };

  const applyFilters = (nextStart, nextEnd) => {
    const s = nextStart || startDate;
    const e = nextEnd || endDate;
    if (!s || !e || !s.isValid() || !e.isValid()) {
      toast.error('Bitte wähle gültige Daten aus');
      return;
    }

    if (s.isAfter(e)) {
      toast.error('Das Startdatum darf nicht nach dem Enddatum liegen');
      return;
    }

    if (ownOnly && hasOwnAssignedBranches && branchOptions.length > 1 && !selectedBranchId) {
      toast.error('Bitte einen Standort auswählen');
      return;
    }

    const branchId = selectedBranchId === '' ? undefined : selectedBranchId || undefined;

    setSelectedCacheEntryId(null);
    setAppliedFilter({
      startDate: s.format('YYYY-MM-DD'),
      endDate: e.format('YYYY-MM-DD'),
      selectedFilter,
      branchId,
    });
  };

  const handleKeyDown = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      applyFilters();
    }
  };

  const appliedType = useMemo(
    () => parseFilter(appliedFilter.selectedFilter).type,
    [appliedFilter.selectedFilter]
  );
  const appliedProductIdRaw = useMemo(
    () => parseFilter(appliedFilter.selectedFilter).productId,
    [appliedFilter.selectedFilter]
  );
  const appliedProductId = appliedProductIdRaw !== 'all' ? appliedProductIdRaw : null;
  const isResourceActive = appliedType === 'resource';

  // Own users with multiple assigned branches must pick one before we query;
  // a single assigned branch is auto-selected on load.
  const ownNeedsBranchPick = ownOnly && hasOwnAssignedBranches && !appliedFilter.branchId;
  const shouldQuery = canView && branchesReady && !ownNeedsBranchPick;

  const balanceFilterParams = useMemo(() => {
    if (isResourceActive || !shouldQuery) return null;
    const p = {
      startDate: appliedFilter.startDate,
      endDate: appliedFilter.endDate,
      voucherProductId: appliedProductId || 'all',
    };
    if (appliedFilter.branchId) p.branchId = appliedFilter.branchId;
    return p;
  }, [
    appliedFilter.startDate,
    appliedFilter.endDate,
    appliedFilter.branchId,
    appliedProductId,
    isResourceActive,
    shouldQuery,
  ]);

  const resourceFilterParams = useMemo(() => {
    if (!isResourceActive || !shouldQuery) return null;
    const p = {
      startDate: appliedFilter.startDate,
      endDate: appliedFilter.endDate,
      resourceVoucherProductId: appliedProductId || 'all',
    };
    if (appliedFilter.branchId) p.branchId = appliedFilter.branchId;
    return p;
  }, [
    appliedFilter.startDate,
    appliedFilter.endDate,
    appliedFilter.branchId,
    appliedProductId,
    isResourceActive,
    shouldQuery,
  ]);

  const balanceJob = useStatisticJob({
    statisticType: STATISTIC_TYPE_VOUCHER,
    endpoint: '/api/v1/voucher/statistics',
    filterParams: balanceFilterParams,
  });

  const resourceJob = useStatisticJob({
    statisticType: STATISTIC_TYPE_RESOURCE_VOUCHER,
    endpoint: '/api/v1/resourceVoucher/statistics',
    filterParams: resourceFilterParams,
  });

  const activeJob = isResourceActive ? resourceJob : balanceJob;
  const stats = activeJob.payload;
  const isFetchingStats =
    !stats && (activeJob.status === 'idle' || activeJob.status === 'fetching');

  useEffect(() => {
    if (activeJob.status === 'failed' && activeJob.error) {
      toast.error(`Fehler beim Laden der Gutscheinstatistiken: ${activeJob.error}`);
    }
  }, [activeJob.status, activeJob.error]);

  const {
    entries: cacheEntries,
    isLoading: cacheEntriesLoading,
  } = useStatisticCacheEntries({
    statisticType: isResourceActive ? STATISTIC_TYPE_RESOURCE_VOUCHER : STATISTIC_TYPE_VOUCHER,
  });

  const handleSelectCacheEntry = useCallback(
    entryId => {
      setSelectedCacheEntryId(entryId);
      if (entryId) {
        activeJob.loadCacheEntry(entryId);
      } else {
        activeJob.fetch();
      }
    },
    [activeJob]
  );

  const isResourceStats = stats?.type === 'resourceVoucher';
  // The backend withholds all sales/issuance figures for the assigned-branch
  // ("own") view and for any branch-scoped Flexticket view, surfacing only
  // redemption data. Render a focused redemption layout in that case.
  const isRedemptionsOnly = !!stats?.redemptionsOnly;

  const downloadReport = useCallback(async () => {
    setIsDownloadingReport(true);
    try {
      const { productId } = parseFilter(appliedFilter.selectedFilter);
      const effectiveProductId = productId !== 'all' ? productId : null;

      const blob = await downloadVoucherStatisticsReport(
        appliedFilter.startDate,
        appliedFilter.endDate,
        effectiveProductId,
        appliedFilter.branchId || null
      );

      if (!blob) {
        return;
      }

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `voucher-report-${appliedFilter.startDate}_${appliedFilter.endDate}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      toast.error('Fehler beim Erstellen des Reports');
    } finally {
      setIsDownloadingReport(false);
    }
  }, [appliedFilter, downloadVoucherStatisticsReport]);

  useEffect(() => {
    setStartDate(dayjs(appliedFilter.startDate));
    setEndDate(dayjs(appliedFilter.endDate));
    setSelectedFilter(appliedFilter.selectedFilter);
  }, [appliedFilter]);

  const chartData = useMemo(() => {
    if (!stats?.timeSeries?.daily) return [];

    const dailyData = stats.timeSeries.daily.filter(Boolean);

    if (chartType === 'line') {
      return dailyData.map(point => ({
        ...point,
        label: dayjs(point.date).format('DD.MM.'),
      }));
    }

    if (!stats.appliedFilter) return [];
    const start = dayjs(stats.appliedFilter.startDate);
    const end = dayjs(stats.appliedFilter.endDate);
    const diffDays = end.diff(start, 'day') + 1;

    let granularity = 'day';
    if (diffDays > 180) granularity = 'month';
    else if (diffDays > 35) granularity = 'week';

    if (granularity === 'day') {
      return dailyData.map(point => ({
        ...point,
        label: dayjs(point.date).format('DD.MM.'),
      }));
    }

    const aggregated = {};
    dailyData.forEach(point => {
      const date = dayjs(point.date);
      let key;
      let label;

      if (granularity === 'week') {
        const weekStart = date.startOf('isoWeek');
        const weekEnd = date.endOf('isoWeek');
        key = weekStart.format('YYYY-MM-DD');
        label = `${weekStart.format('DD.MM.')} – ${weekEnd.format('DD.MM.')}`;
      } else {
        key = date.format('YYYY-MM');
        label = date.format('MMM YYYY');
      }

      if (!aggregated[key]) {
        aggregated[key] = {
          date: key,
          label,
          ...(isResourceStats
            ? { issuedCount: 0, redeemedCount: 0 }
            : { soldCount: 0, soldAmount: 0, redeemedCount: 0, redeemedAmount: 0 }),
        };
      }

      if (isResourceStats) {
        aggregated[key].issuedCount += point.issuedCount || 0;
        aggregated[key].redeemedCount += point.redeemedCount || 0;
      } else {
        aggregated[key].soldCount += point.soldCount || 0;
        aggregated[key].soldAmount += point.soldAmount || 0;
        aggregated[key].redeemedCount += point.redeemedCount || 0;
        aggregated[key].redeemedAmount += point.redeemedAmount || 0;
      }
    });

    return Object.values(aggregated).sort((a, b) => a.date.localeCompare(b.date));
  }, [stats, chartType, isResourceStats]);

  const hasChartData = chartData.length > 0;

  const handleChartTypeChange = (_, value) => {
    if (value) {
      setChartType(value);
    }
  };

  const balanceCards = useMemo(() => {
    if (!stats || isResourceStats) return [];
    if (isRedemptionsOnly) {
      return [
        {
          label: 'Einlösungen',
          value: stats.redeemedCount,
          helper: 'Anzahl eingelöster Gutscheine in dieser Filiale',
        },
        {
          label: 'Eingelöster Umsatz',
          value: formatCurrency(stats.redeemedRevenue),
          helper: 'Summe der Einlösungen in dieser Filiale',
        },
        {
          label: 'Ø Einlösedauer',
          value: stats.averageRedemptionTimeDays
            ? `${decimalFormatter.format(stats.averageRedemptionTimeDays)} Tage`
            : '0,0 Tage',
          helper: 'Zeit zwischen Aufladung und erster Einlösung',
        },
      ];
    }
    return [
      {
        label: 'Verkaufte Gutscheine',
        value: stats.soldCount,
        helper: 'Anzahl verkaufter Gutscheine im Zeitraum',
      },
      {
        label: 'Einlösungen',
        value: stats.redeemedCount,
        helper: 'Anzahl eingelöster Gutscheine',
      },
      {
        label: 'Verkauft & eingelöst',
        value: stats.soldAndRedeemedCount,
        helper: 'Gutscheine mit beiden Ereignissen im Zeitraum',
      },
      {
        label: 'Gesamtumsatz',
        value: formatCurrency(stats.soldRevenue),
        helper: 'Summe aller Verkäufe',
      },
      {
        label: 'Nicht eingelöster Umsatz',
        value: formatCurrency(stats.unredeemedRevenue),
        helper: 'Aufgeladene Beträge ohne Einlösung',
      },
      {
        label: 'Durchschnittlicher Gutscheinwert',
        value: formatCurrency(stats.averageVoucherValue),
        helper: 'Umsatz pro verkauften Gutschein',
      },
      {
        label: 'Ø Einlösedauer',
        value: stats.averageRedemptionTimeDays
          ? `${decimalFormatter.format(stats.averageRedemptionTimeDays)} Tage`
          : '0,0 Tage',
        helper: 'Zeit zwischen Aufladung und erster Einlösung',
      },
      {
        label: 'Bezahlter Anteil',
        value: stats.giftVsPaid
          ? `${percentFormatter.format(stats.giftVsPaid.paidShare)} %`
          : '0,0 %',
        helper: 'Anteil bezahlter Werte am Umsatz',
      },
    ];
  }, [stats, isResourceStats, isRedemptionsOnly]);

  const resourceCards = useMemo(() => {
    if (!stats || !isResourceStats) return [];
    if (isRedemptionsOnly) {
      return [
        {
          label: 'Eingelöste Gutscheine',
          value: stats.redeemedCount,
          helper: 'Anzahl eingelöster Flextickets in dieser Filiale',
        },
      ];
    }
    return [
      {
        label: 'Ausgestellte Gutscheine',
        value: stats.issuedCount,
        helper: 'Anzahl ausgestellter Flextickets im Zeitraum',
      },
      {
        label: 'Eingelöste Gutscheine',
        value: stats.redeemedCount,
        helper: 'Anzahl eingelöster Flextickets',
      },
      {
        label: 'Verkauft',
        value: stats.soldCount,
        helper: 'Bezahlte Gutscheine',
      },
      {
        label: 'Verschenkt',
        value: stats.giftedCount,
        helper: 'Kostenlos ausgestellte Gutscheine',
      },
      {
        label: 'Umsatz (Listenpreise)',
        value: formatCurrency(stats.salesRevenue),
        helper: 'Geschätzter Umsatz basierend auf Produkt-Listenpreisen',
      },
      {
        label: 'Gesamte Personenanzahl',
        value: stats.totalPersons,
        helper: 'Personen auf allen ausgestellten Gutscheinen',
      },
      {
        label: 'Ø Einlösedauer',
        value: stats.averageRedemptionTimeDays
          ? `${decimalFormatter.format(stats.averageRedemptionTimeDays)} Tage`
          : '0,0 Tage',
        helper: 'Zeit zwischen Ausstellung und Einlösung',
      },
      {
        label: 'Einlösungsrate',
        value: `${percentFormatter.format(stats.redemptionRate ?? 0)} %`,
        helper: 'Verhältnis eingelöster zu ausgestellten Gutscheinen',
      },
    ];
  }, [stats, isResourceStats, isRedemptionsOnly]);

  const cards = isResourceStats ? resourceCards : balanceCards;

  if (!canView) {
    return <Navigate to='/welcome' replace />;
  }

  const renderKPICards = () => {
    if (!stats) return null;

    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '1rem',
        }}
      >
        {cards.map(card => (
          <article key={card.label} style={cardStyle}>
            <span style={{ fontSize: '0.85rem', color: kpiLabelColor }}>{card.label}</span>
            <strong style={{ fontSize: '1.5rem' }}>{card.value}</strong>
            <small style={{ color: kpiHelperColor }}>{card.helper}</small>
          </article>
        ))}

        {!isResourceStats && !isRedemptionsOnly && (
          <>
            <article style={{ ...cardStyle, gap: '0.35rem' }}>
              <span style={{ fontSize: '0.85rem', color: kpiLabelColor }}>Einlösungsrate</span>
              <strong style={{ fontSize: '1.5rem' }}>
                {stats.redemptionRate
                  ? `${percentFormatter.format(stats.redemptionRate)} %`
                  : '0,0 %'}
              </strong>
              <span style={{ fontSize: '0.85rem', color: kpiHelperColor }}>
                Verhältnis eingelöster zu verkauften Gutscheinen
              </span>
            </article>
            <article style={{ ...cardStyle, gap: '0.35rem' }}>
              <span style={{ fontSize: '0.85rem', color: kpiLabelColor }}>Nicht eingelöster Anteil</span>
              <strong style={{ fontSize: '1.5rem' }}>
                {stats.unredeemedRevenueShare
                  ? `${percentFormatter.format(stats.unredeemedRevenueShare)} %`
                  : '0,0 %'}
              </strong>
              <span style={{ fontSize: '0.85rem', color: kpiHelperColor }}>
                Anteil des Umsatzes, der noch auf Gutscheinen liegt
              </span>
            </article>
          </>
        )}
      </div>
    );
  };

  const renderChart = () => {
    if (!stats) return null;

    const chartDataKeyA = isResourceStats ? 'issuedCount' : 'soldAmount';
    const chartDataKeyB = isResourceStats ? 'redeemedCount' : 'redeemedAmount';
    const chartLabelA = isResourceStats ? 'Ausgestellt' : 'Verkäufe (€)';
    const chartLabelB = isResourceStats ? 'Eingelöst' : 'Einlösungen (€)';
    const tooltipFmt = isResourceStats ? value => value : value => formatCurrency(value);

    return (
      <section>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent='space-between'
          sx={{ mb: 1 }}
        >
          <Typography variant='h6' component='h2' sx={{ m: 0 }}>
            {isRedemptionsOnly
              ? 'Einlösungsverlauf'
              : isResourceStats
                ? 'Ausstellungs- & Einlösungsverlauf'
                : 'Verkaufs- & Einlösungsverlauf'}
          </Typography>
          <ToggleButtonGroup
            value={chartType}
            exclusive
            size='small'
            onChange={handleChartTypeChange}
          >
            <ToggleButton value='line'>Linie</ToggleButton>
            <ToggleButton value='bar'>Balken</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        {hasChartData ? (
          <Paper elevation={3} sx={{ p: 2, background: statSurfaceBg, height: 360 }}>
            <ResponsiveContainer width='100%' height='100%'>
              {chartType === 'line' ? (
                <LineChart data={chartData}>
                  <CartesianGrid stroke={chartGridStroke} />
                  <XAxis dataKey='label' stroke={axisStroke} />
                  <YAxis stroke={axisStroke} />
                  <Tooltip
                    contentStyle={chartTooltipContentStyle}
                    labelStyle={tooltipLabelStyle}
                    formatter={tooltipFmt}
                  />
                  <Legend {...chartLegendBarProps} />
                  {!isRedemptionsOnly && (
                    <Line
                      type='monotone'
                      dataKey={chartDataKeyA}
                      name={chartLabelA}
                      stroke={sellAndRedeemColors[0]}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  )}
                  <Line
                    type='monotone'
                    dataKey={chartDataKeyB}
                    name={chartLabelB}
                    stroke={sellAndRedeemColors[1]}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                </LineChart>
              ) : (
                <BarChart data={chartData}>
                  <CartesianGrid stroke={chartGridStroke} vertical={false} />
                  <XAxis dataKey='label' stroke={axisStroke} />
                  <YAxis stroke={axisStroke} />
                  <Tooltip
                    cursor={{ fill: chartCursorFill }}
                    contentStyle={chartTooltipContentStyle}
                    labelStyle={tooltipLabelStyle}
                    formatter={tooltipFmt}
                  />
                  <Legend {...chartLegendBarProps} />
                  {!isRedemptionsOnly && (
                    <Bar
                      dataKey={chartDataKeyA}
                      name={chartLabelA}
                      fill={sellAndRedeemColors[0]}
                    />
                  )}
                  <Bar
                    dataKey={chartDataKeyB}
                    name={chartLabelB}
                    fill={sellAndRedeemColors[1]}
                  />
                </BarChart>
              )}
            </ResponsiveContainer>
          </Paper>
        ) : (
          <Typography variant='body2' color='text.secondary'>
            Keine Zeitreihendaten verfügbar.
          </Typography>
        )}
      </section>
    );
  };

  const renderBalancePieCharts = () => {
    if (!stats?.giftVsPaid) return null;

    return (
      <section>
        <Typography variant='h6' component='h2' sx={{ mb: 2 }}>
          Werteverteilung & Umsatzstatus
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                background: statSurfaceBg,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography variant='subtitle1' component='h3' sx={{ fontWeight: 600 }}>
                Bezahlte vs. Geschenkte Werte
              </Typography>
              <div style={{ width: '100%', height: 250 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Bezahlt', value: stats.giftVsPaid.paidAmount },
                        { name: 'Geschenkt', value: stats.giftVsPaid.giftedAmount },
                      ]}
                      cx='50%'
                      cy='50%'
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey='value'
                      stroke='none'
                    >
                      <Cell fill={colorsPaidGift[0]} />
                      <Cell fill={colorsPaidGift[1]} />
                    </Pie>
                    <Tooltip
                      formatter={value => formatCurrency(value)}
                      contentStyle={chartTooltipContentStyle}
                    />
                    <Legend {...chartLegendPieProps} verticalAlign='bottom' height={36} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <Box sx={{ mt: 2, width: '100%' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    borderBottom: tableRowBorderTop,
                    padding: '0.5rem 0',
                  }}
                >
                  <span>Bezahlt</span>
                  <strong>
                    {formatCurrency(stats.giftVsPaid.paidAmount)} (
                    {percentFormatter.format(stats.giftVsPaid.paidShare)} %)
                  </strong>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '0.5rem 0',
                  }}
                >
                  <span>Geschenkt</span>
                  <strong>
                    {formatCurrency(stats.giftVsPaid.giftedAmount)} (
                    {percentFormatter.format(stats.giftVsPaid.giftedShare)} %)
                  </strong>
                </div>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                background: statSurfaceBg,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography variant='subtitle1' component='h3' sx={{ fontWeight: 600 }}>
                Umsatzstatus
              </Typography>
              <div style={{ width: '100%', height: 250 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={[
                        {
                          name: 'Bereits eingelöst',
                          value: Math.max(0, stats.soldRevenue - stats.unredeemedRevenue),
                        },
                        { name: 'Ausstehend', value: stats.unredeemedRevenue },
                      ]}
                      cx='50%'
                      cy='50%'
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey='value'
                      stroke='none'
                    >
                      <Cell fill={colorsRevenue[0]} />
                      <Cell fill={colorsRevenue[1]} />
                    </Pie>
                    <Tooltip
                      formatter={value => formatCurrency(value)}
                      contentStyle={chartTooltipContentStyle}
                    />
                    <Legend {...chartLegendPieProps} verticalAlign='bottom' height={36} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <Box sx={{ mt: 2, width: '100%' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    borderBottom: tableRowBorderTop,
                    padding: '0.5rem 0',
                  }}
                >
                  <span>Bereits eingelöst</span>
                  <strong>
                    {formatCurrency(Math.max(0, stats.soldRevenue - stats.unredeemedRevenue))} (
                    {percentFormatter.format(
                      Math.max(0, 100 - (stats.unredeemedRevenueShare || 0))
                    )}
                     %)
                  </strong>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '0.5rem 0',
                  }}
                >
                  <span>Ausstehend</span>
                  <strong>
                    {formatCurrency(stats.unredeemedRevenue)} (
                    {percentFormatter.format(stats.unredeemedRevenueShare)} %)
                  </strong>
                </div>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </section>
    );
  };

  const renderResourcePieCharts = () => {
    if (!stats || !isResourceStats) return null;

    const soldVsGiftedData = [
      { name: 'Verkauft', value: stats.soldCount || 0 },
      { name: 'Verschenkt', value: stats.giftedCount || 0 },
    ].filter(d => d.value > 0);

    const statusData = Object.entries(stats.statusBreakdown || {})
      .filter(([key, val]) => val > 0 && key !== 'waitingForPayment')
      .map(([key, val]) => ({
        name: STATUS_LABELS[key] || key,
        value: val,
        color: statusColors[key] || theme.palette.grey[500],
      }));

    const hasSoldGiftedData = soldVsGiftedData.length > 0;
    const hasStatusData = statusData.length > 0;

    if (!hasSoldGiftedData && !hasStatusData) return null;

    return (
      <section>
        <Typography variant='h6' component='h2' sx={{ mb: 2 }}>
          Verteilung
        </Typography>
        <Grid container spacing={2}>
          {hasSoldGiftedData && (
            <Grid item xs={12} md={6}>
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  background: statSurfaceBg,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Typography variant='subtitle1' component='h3' sx={{ fontWeight: 600 }}>
                  Verkauft vs. Verschenkt
                </Typography>
                <div style={{ width: '100%', height: 250 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={soldVsGiftedData}
                        cx='50%'
                        cy='50%'
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey='value'
                        stroke='none'
                      >
                        {soldVsGiftedData.map((entry, index) => (
                          <Cell
                            key={entry.name}
                            fill={index === 0 ? colorsPaidGift[0] : colorsPaidGift[1]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={chartTooltipContentStyle}
                      />
                      <Legend {...chartLegendPieProps} verticalAlign='bottom' height={36} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <Box sx={{ mt: 2, width: '100%' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      borderBottom: tableRowBorderTop,
                      padding: '0.5rem 0',
                    }}
                  >
                    <span>Verkauft</span>
                    <strong>
                      {stats.soldCount} (
                      {stats.issuedCount > 0
                        ? percentFormatter.format(
                            (stats.soldCount / stats.issuedCount) * 100
                          )
                        : '0,0'}
                       %)
                    </strong>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '0.5rem 0',
                    }}
                  >
                    <span>Verschenkt</span>
                    <strong>
                      {stats.giftedCount} (
                      {stats.issuedCount > 0
                        ? percentFormatter.format(
                            (stats.giftedCount / stats.issuedCount) * 100
                          )
                        : '0,0'}
                       %)
                    </strong>
                  </div>
                </Box>
              </Paper>
            </Grid>
          )}

          {hasStatusData && (
            <Grid item xs={12} md={6}>
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  background: statSurfaceBg,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Typography variant='subtitle1' component='h3' sx={{ fontWeight: 600 }}>
                  Statusverteilung
                </Typography>
                <div style={{ width: '100%', height: 250 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={statusData}
                        cx='50%'
                        cy='50%'
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey='value'
                        stroke='none'
                      >
                        {statusData.map(entry => (
                          <Cell key={entry.name} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={chartTooltipContentStyle}
                      />
                      <Legend {...chartLegendPieProps} verticalAlign='bottom' height={36} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <Box sx={{ mt: 2, width: '100%' }}>
                  {statusData.map((entry, idx) => (
                    <div
                      key={entry.name}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        borderBottom: idx < statusData.length - 1 ? tableRowBorderTop : 'none',
                        padding: '0.5rem 0',
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            background: entry.color,
                            display: 'inline-block',
                          }}
                        />
                        {entry.name}
                      </span>
                      <strong>{entry.value}</strong>
                    </div>
                  ))}
                </Box>
              </Paper>
            </Grid>
          )}
        </Grid>
      </section>
    );
  };

  const renderBranchRedemptions = () => {
    if (!stats?.branchRedemptions?.length) return null;

    return (
      <section>
        <Typography variant='h6' component='h2' sx={{ mb: 2 }}>
          Einlösungen nach Filiale
        </Typography>
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              minWidth: '480px',
            }}
          >
            <thead>
              <tr>
                <th style={htmlThStyle()}>Filiale</th>
                <th style={htmlThStyle({ textAlign: 'right' })}>Einlösungen</th>
                {!isResourceStats && (
                  <>
                    <th style={htmlThStyle({ textAlign: 'right' })}>Umsatz</th>
                    <th style={htmlThStyle({ textAlign: 'right' })}>Ø Bon</th>
                  </>
                )}
                <th style={htmlThStyle({ textAlign: 'right' })}>Anteil</th>
              </tr>
            </thead>
            <tbody>
              {stats.branchRedemptions.map(branch => (
                <tr key={branch.branchId || branch.branchName}>
                  <td style={{ padding: '0.75rem', borderTop: tableRowBorderTop }}>
                    {branch.branchName}
                  </td>
                  <td
                    style={{
                      padding: '0.75rem',
                      borderTop: tableRowBorderTop,
                      textAlign: 'right',
                    }}
                  >
                    {branch.redeemedCount}
                  </td>
                  {!isResourceStats && (
                    <>
                      <td
                        style={{
                          padding: '0.75rem',
                          borderTop: tableRowBorderTop,
                          textAlign: 'right',
                        }}
                      >
                        {formatCurrency(branch.redeemedAmount)}
                      </td>
                      <td
                        style={{
                          padding: '0.75rem',
                          borderTop: tableRowBorderTop,
                          textAlign: 'right',
                        }}
                      >
                        {formatCurrency(branch.averageRedeemAmount)}
                      </td>
                    </>
                  )}
                  <td
                    style={{
                      padding: '0.75rem',
                      borderTop: tableRowBorderTop,
                      textAlign: 'right',
                    }}
                  >
                    {percentFormatter.format(branch.revenueShare)} %
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    );
  };

  const renderBalanceTopProducts = () => {
    if (!stats?.topSellingProducts?.length || isResourceStats) return null;

    return (
      <section>
        <Typography variant='h6' component='h2' sx={{ mb: 2 }}>
          Top Gutscheinprodukte
        </Typography>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
          {stats.topSellingProducts.map(product => (
            <div key={product.productId} style={{ ...cardStyle, flex: '1 1 250px', gap: '0.35rem' }}>
              <strong>{product.productName}</strong>
              <Typography variant='body2' sx={{ mt: 1, color: secondaryTextColor }}>
                Umsatz: {formatCurrency(product.revenue)}
              </Typography>
              <Typography variant='body2' sx={{ mt: 0.5, color: kpiHelperColor }}>
                Verkäufe: {product.soldCount}
              </Typography>
              <Typography variant='body2' sx={{ mt: 0.5, color: kpiHelperColor }}>
                Einlösungen: {product.redeemedCount} (
                {percentFormatter.format(product.conversionRate)} %)
              </Typography>
              <Typography variant='body2' sx={{ mt: 0.5, color: kpiHelperColor }}>
                Bezahlt: {formatCurrency(product.paidAmount)} (
                {percentFormatter.format(product.paidShare)} %)
              </Typography>
              <Typography variant='body2' sx={{ mt: 0.5, color: kpiHelperColor }}>
                Geschenkt: {formatCurrency(product.giftedAmount)} (
                {percentFormatter.format(product.giftShare)} %)
              </Typography>
            </div>
          ))}
        </div>
      </section>
    );
  };

  const renderResourceTopProducts = () => {
    if (!stats?.topProducts?.length || !isResourceStats) return null;

    return (
      <section>
        <Typography variant='h6' component='h2' sx={{ mb: 2 }}>
          Top Flexticket-Produkte
        </Typography>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
          {stats.topProducts.map(product => (
            <div
              key={product.productId}
              style={{ ...cardStyle, flex: '1 1 250px', gap: '0.35rem' }}
            >
              <strong>{product.productName}</strong>
              <Typography variant='body2' sx={{ mt: 1, color: secondaryTextColor }}>
                Listenpreis: {formatCurrency(product.basePrice)}
              </Typography>
              <Typography variant='body2' sx={{ mt: 0.5, color: kpiHelperColor }}>
                Ausgestellt: {product.issuedCount} (davon {product.soldCount} verkauft,{' '}
                {product.giftedCount} verschenkt)
              </Typography>
              <Typography variant='body2' sx={{ mt: 0.5, color: kpiHelperColor }}>
                Eingelöst: {product.redeemedCount} (
                {percentFormatter.format(product.conversionRate)} %)
              </Typography>
              <Typography variant='body2' sx={{ mt: 0.5, color: kpiHelperColor }}>
                Personen: {product.totalPersons}
              </Typography>
            </div>
          ))}
        </div>
      </section>
    );
  };

  const renderRegionImportSection = () => {
    const ri = stats?.regiondoImport;
    if (!ri || !isResourceStats || ri.totalCount === 0) return null;

    return (
      <section>
        <Typography variant='h6' component='h2' sx={{ mb: 1 }}>
          Regiondo-Import (nicht in Kennzahlen enthalten)
        </Typography>
        <Typography variant='body2' sx={{ color: kpiHelperColor, mt: 0 }}>
          Diese Gutscheine wurden aus Regiondo importiert. Das Ausstellungsdatum entspricht dem
          Importdatum, nicht dem tatsächlichen Kaufdatum. Sie werden daher separat ausgewiesen.
        </Typography>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '1rem',
          }}
        >
          <article style={cardStyle}>
            <span style={{ fontSize: '0.85rem', color: kpiLabelColor }}>Importiert gesamt</span>
            <strong style={{ fontSize: '1.5rem' }}>{ri.totalCount}</strong>
            <small style={{ color: kpiHelperColor }}>Alle aus Regiondo importierten Flextickets</small>
          </article>
          <article style={cardStyle}>
            <span style={{ fontSize: '0.85rem', color: kpiLabelColor }}>
              Importiert im Zeitraum
            </span>
            <strong style={{ fontSize: '1.5rem' }}>{ri.importedCount}</strong>
            <small style={{ color: kpiHelperColor }}>
              {ri.totalPersons} Personen
            </small>
          </article>
          <article style={cardStyle}>
            <span style={{ fontSize: '0.85rem', color: kpiLabelColor }}>Eingelöst im Zeitraum</span>
            <strong style={{ fontSize: '1.5rem' }}>{ri.redeemedCount}</strong>
            <small style={{ color: kpiHelperColor }}>Einlösungen von Regiondo-Gutscheinen</small>
          </article>
          {Object.entries(ri.statusBreakdown)
            .filter(([, val]) => val > 0)
            .map(([key, val]) => (
              <article key={key} style={cardStyle}>
                <span style={{ fontSize: '0.85rem', color: kpiLabelColor }}>
                  {STATUS_LABELS[key] || key}
                </span>
                <strong style={{ fontSize: '1.5rem' }}>{val}</strong>
                <small style={{ color: kpiHelperColor }}>Aktueller Status</small>
              </article>
            ))}
        </div>
      </section>
    );
  };

  const renderBalanceSpecialKinds = () => {
    if (!stats?.specialKinds || isResourceStats) return null;

    return (
      <section>
        <Typography variant='h6' component='h2' sx={{ mb: 1 }}>
          Sonderfälle (nicht in Kennzahlen enthalten)
        </Typography>
        <Typography variant='body2' sx={{ color: kpiHelperColor, mt: 0 }}>
          No-show Kulanz, Kulanz-/Lokalmarketing- und Kundenfeedback-Kulanz-Gutscheine werden aus
          der normalen Statistik herausgerechnet und hier separat ausgewiesen.
        </Typography>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '1rem',
            marginBottom: '1rem',
          }}
        >
          {[
            stats.specialKinds.noShowCompensation,
            stats.specialKinds.giftedBalance,
            stats.specialKinds.feedbackCompensation,
          ]
            .filter(Boolean)
            .map(item => (
              <article key={item.kind} style={{ ...cardStyle, gap: '0.35rem' }}>
                <strong style={{ fontSize: '1rem' }}>{item.label}</strong>
                <span style={{ color: secondaryTextColor }}>
                  Ausgaben: <strong>{item.issuedCount}</strong>
                </span>
                <span style={{ color: secondaryTextColor }}>
                  Summe: <strong>{formatCurrency(item.issuedAmount)}</strong>
                </span>
                <small style={{ color: kpiHelperColor }}>
                  Normal: {formatCurrency(item.issuedNormalAmount)} · Gifted:{' '}
                  {formatCurrency(item.issuedGiftedAmount)}
                </small>
              </article>
            ))}
        </div>

        {[
          stats.specialKinds.noShowCompensation,
          stats.specialKinds.giftedBalance,
          stats.specialKinds.feedbackCompensation,
        ]
          .filter(Boolean)
          .map(item => (
            <div key={`${item.kind}-table`} style={{ marginBottom: '1.5rem' }}>
              <Typography variant='subtitle1' component='h3' sx={{ my: 1.5, fontWeight: 600 }}>
                {item.label}: Ausgaben nach Filiale
              </Typography>
              {item.issuedByBranch && item.issuedByBranch.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table
                    style={{ width: '100%', borderCollapse: 'collapse', minWidth: '560px' }}
                  >
                    <thead>
                      <tr>
                        <th style={htmlThStyle()}>Filiale</th>
                        <th style={htmlThStyle({ textAlign: 'right' })}>Ausgaben</th>
                        <th style={htmlThStyle({ textAlign: 'right' })}>Summe</th>
                        <th style={htmlThStyle({ textAlign: 'right' })}>Normal</th>
                        <th style={htmlThStyle({ textAlign: 'right' })}>Gifted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {item.issuedByBranch.map(row => (
                        <tr key={`${item.kind}-${row.branchId || row.branchName}`}>
                          <td style={{ padding: '0.75rem', borderTop: tableRowBorderTop }}>
                            {row.branchName}
                          </td>
                          <td
                            style={{
                              padding: '0.75rem',
                              borderTop: tableRowBorderTop,
                              textAlign: 'right',
                            }}
                          >
                            {row.issuedCount}
                          </td>
                          <td
                            style={{
                              padding: '0.75rem',
                              borderTop: tableRowBorderTop,
                              textAlign: 'right',
                            }}
                          >
                            {formatCurrency(row.issuedAmount)}
                          </td>
                          <td
                            style={{
                              padding: '0.75rem',
                              borderTop: tableRowBorderTop,
                              textAlign: 'right',
                            }}
                          >
                            {formatCurrency(row.issuedNormalAmount)}
                          </td>
                          <td
                            style={{
                              padding: '0.75rem',
                              borderTop: tableRowBorderTop,
                              textAlign: 'right',
                            }}
                          >
                            {formatCurrency(row.issuedGiftedAmount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <Typography variant='body2' color='text.secondary'>
                  Keine Daten im Zeitraum.
                </Typography>
              )}
            </div>
          ))}

        {stats?.giftedBalanceRequests && (
          <div style={{ marginBottom: '1.5rem' }}>
            <Typography variant='subtitle1' component='h3' sx={{ my: 1.5, fontWeight: 600 }}>
              Kulanz-/Marketing: Freigabeanfragen
            </Typography>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '1rem',
                marginBottom: '1rem',
              }}
            >
              {[
                {
                  label: 'Anfragen gesamt',
                  count: stats.giftedBalanceRequests.totalCount,
                  amount: stats.giftedBalanceRequests.totalAmount,
                },
                {
                  label: 'Pending',
                  count: stats.giftedBalanceRequests.pendingCount,
                  amount: stats.giftedBalanceRequests.pendingAmount,
                },
                {
                  label: 'Approved',
                  count: stats.giftedBalanceRequests.approvedCount,
                  amount: stats.giftedBalanceRequests.approvedAmount,
                },
              ].map(item => (
                <article key={item.label} style={cardStyle}>
                  <span style={{ fontSize: '0.85rem', color: kpiLabelColor }}>{item.label}</span>
                  <strong style={{ fontSize: '1.5rem' }}>{item.count}</strong>
                  <small style={{ color: kpiHelperColor }}>Summe: {formatCurrency(item.amount)}</small>
                </article>
              ))}
            </div>

            {stats.giftedBalanceRequests.byBranch &&
            stats.giftedBalanceRequests.byBranch.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <table
                  style={{ width: '100%', borderCollapse: 'collapse', minWidth: '560px' }}
                >
                  <thead>
                    <tr>
                      <th style={htmlThStyle()}>Filiale</th>
                      <th style={htmlThStyle()}>Status</th>
                      <th style={htmlThStyle({ textAlign: 'right' })}>Anzahl</th>
                      <th style={htmlThStyle({ textAlign: 'right' })}>Summe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.giftedBalanceRequests.byBranch.map(row => (
                      <tr key={`${row.branchId || row.branchName}-${row.status}`}>
                        <td style={{ padding: '0.75rem', borderTop: tableRowBorderTop }}>
                          {row.branchName}
                        </td>
                        <td style={{ padding: '0.75rem', borderTop: tableRowBorderTop }}>
                          {row.status}
                        </td>
                        <td
                          style={{
                            padding: '0.75rem',
                            borderTop: tableRowBorderTop,
                            textAlign: 'right',
                          }}
                        >
                          {row.count}
                        </td>
                        <td
                          style={{
                            padding: '0.75rem',
                            borderTop: tableRowBorderTop,
                            textAlign: 'right',
                          }}
                        >
                          {formatCurrency(row.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <Typography variant='body2' color='text.secondary'>
                Keine Freigabeanfragen im Zeitraum.
              </Typography>
            )}
          </div>
        )}
      </section>
    );
  };

  const renderBalanceReportSummary = () => {
    if (!stats?.reportSummary || isResourceStats) return null;

    return (
      <section>
        <Typography variant='h6' component='h2' sx={{ mb: 2 }}>
          Report-Zusammenfassung
        </Typography>
        <Paper elevation={3} sx={{ p: 3, background: statSurfaceBg }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <strong>Highlights</strong>
              <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem', color: secondaryTextColor }}>
                {stats.reportSummary.highlights.map((item, index) => (
                  <li key={`${item}-${index}`}>{item}</li>
                ))}
              </ul>
            </Grid>
            <Grid item xs={12} md={6}>
              <strong>Insights</strong>
              <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem', color: secondaryTextColor }}>
                {stats.reportSummary.insights.map((item, index) => (
                  <li key={`${item}-${index}`}>{item}</li>
                ))}
              </ul>
            </Grid>
          </Grid>
        </Paper>
      </section>
    );
  };


  const voucherFilterParamLookups = useMemo(() => {
    const voucherMap = { all: 'Alle Wertgutscheine' };
    for (const p of voucherProducts) voucherMap[String(p._id)] = p.name;
    const resourceMap = { all: 'Alle Flextickets' };
    for (const p of resourceVoucherProducts) resourceMap[String(p._id)] = p.name;
    return {
      voucherProductId: voucherMap,
      resourceVoucherProductId: resourceMap,
    };
  }, [voucherProducts, resourceVoucherProducts]);

  // Own users do not load the product catalogue; they only switch between the
  // two voucher kinds. Full users get the full per-product dropdown.
  const voucherProductFilter = ownOnly ? (
    <FormControl fullWidth>
      <InputLabel id='voucher-type-label'>Gutscheinart</InputLabel>
      <Select
        labelId='voucher-type-label'
        value={selectedFilter}
        label='Gutscheinart'
        onChange={event => setSelectedFilter(event.target.value)}
        onKeyDown={handleKeyDown}
      >
        <MenuItem value='balance:all'>Wertgutscheine</MenuItem>
        <MenuItem value='resource:all'>Flextickets</MenuItem>
      </Select>
    </FormControl>
  ) : (
    <FormControl fullWidth>
      <InputLabel id='voucher-product-label'>Gutscheinprodukt</InputLabel>
      <Select
        labelId='voucher-product-label'
        value={selectedFilter}
        label='Gutscheinprodukt'
        onChange={event => setSelectedFilter(event.target.value)}
        onKeyDown={handleKeyDown}
      >
        <ListSubheader>Wertgutscheine</ListSubheader>
        <MenuItem value='balance:all'>Alle Wertgutscheine</MenuItem>
        {voucherProducts.map(product => (
          <MenuItem key={product._id} value={`balance:${product._id}`}>
            {product.name}
          </MenuItem>
        ))}
        <ListSubheader>Flextickets</ListSubheader>
        <MenuItem value='resource:all'>Alle Flextickets</MenuItem>
        {resourceVoucherProducts.map(product => (
          <MenuItem key={product._id} value={`resource:${product._id}`}>
            {product.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  // The PDF report is sales-oriented and only offered to full-access users.
  const voucherExtraActions = canFullStat && !isResourceStats ? (
    <Button
      variant='outlined'
      onClick={downloadReport}
      disabled={isDownloadingReport}
      aria-label='Report herunterladen'
      sx={{ minWidth: 56, minHeight: 56, padding: 0, borderRadius: 2 }}
      title={isDownloadingReport ? 'Report wird erstellt …' : 'Report herunterladen'}
    >
      {isDownloadingReport ? (
        <CircularProgress size={18} color='inherit' />
      ) : (
        <DownloadIcon fontSize='small' />
      )}
    </Button>
  ) : null;

  const shellFilterParamConfig = { redemptionsOnly: { hidden: true } };

  return (
    <StatisticViewShell
      startDate={startDate}
      endDate={endDate}
      onStartDateChange={setStartDate}
      onEndDateChange={setEndDate}
      branchOptions={branchOptions}
      selectedBranchId={selectedBranchId}
      onBranchChange={setSelectedBranchId}
      branchSelectDisabled={ownOnly && hasOwnAssignedBranches && branchOptions.length === 1}
      branchSelectIdPrefix='voucher-branch'
      extraFilters={voucherProductFilter}
      onApply={applyFilters}
      applyExtraActions={voucherExtraActions}
      status={activeJob.status}
      isStale={activeJob.isStale}
      cacheComputedAt={activeJob.cacheComputedAt}
      cacheExpiresAt={activeJob.cacheExpiresAt}
      job={activeJob.job}
      error={activeJob.error}
      cacheEntries={cacheEntries}
      selectedEntryId={selectedCacheEntryId}
      onSelectEntry={handleSelectCacheEntry}
      entriesLoading={cacheEntriesLoading}
      onRefresh={activeJob.refresh}
      filterParamLookups={voucherFilterParamLookups}
      filterParamConfig={shellFilterParamConfig}
      externallyLoading={!branchesReady}
      hasPayload={!!stats}
    >
      {isRedemptionsOnly && (
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            background: statSurfaceBg,
          }}
        >
          <Typography variant='body2' color='text.secondary'>
            Diese Ansicht zeigt ausschließlich die <strong>Einlösungen</strong> für die ausgewählte
            Filiale.
          </Typography>
        </Paper>
      )}

      <section>{renderKPICards()}</section>

      {!isResourceStats && renderBalanceSpecialKinds()}
      {isResourceStats && renderRegionImportSection()}
      {!isResourceStats && renderBalanceReportSummary()}

      {renderChart()}

      {isResourceStats ? renderResourcePieCharts() : renderBalancePieCharts()}

      {renderBranchRedemptions()}

      {isResourceStats ? renderResourceTopProducts() : renderBalanceTopProducts()}
    </StatisticViewShell>
  );
};

export default VoucherStatistics;
